import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import GUI from 'lil-gui'
import gsap from 'gsap'
import particlesVertexShader from './shaders/particles/vertex.glsl'
import particlesFragmentShader from './shaders/particles/fragment.glsl'

/**
 * Loader UI
 */
const loaderElement = document.getElementById('loader')
const loaderPercent = document.getElementById('loader-percent')
let loadProgress = 0
let isLoaded = false

function updateLoader(progress) {
    loadProgress = Math.min(progress, 99)
    loaderPercent.textContent = Math.floor(loadProgress)
}

function completeLoader() {
    if (isLoaded) return
    isLoaded = true

    // First go to 99%
    gsap.to({ value: loadProgress }, {
        value: 99,
        duration: 0.3,
        onUpdate: function() {
            loaderPercent.textContent = Math.floor(this.targets()[0].value)
        },
        onComplete: () => {
            // Wait 1 second then go to 100%
            setTimeout(() => {
                gsap.to({ value: 99 }, {
                    value: 100,
                    duration: 0.2,
                    onUpdate: function() {
                        loaderPercent.textContent = Math.floor(this.targets()[0].value)
                    },
                    onComplete: () => {
                        // Hide loader
                        setTimeout(() => {
                            loaderElement.classList.add('loaded')
                        }, 200)
                    }
                })
            }, 1000)
        }
    })
}

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 340 })
const debugObject = {}

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loaders
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Materials
    if (particles) {
        particles.material.uniforms.uResolution.value.set(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)
    }
    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 8 * 2)
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.05

// Disable pan (2 finger drag)
controls.enablePan = false

// Limit Z rotation only (dolly/zoom)
controls.minDistance = 8   // Minimum zoom (closest)
controls.maxDistance = 25  // Maximum zoom (farthest)

// No limits on X and Y rotation (polar and azimuth angles)
// This allows full rotation around the object

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
})

renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)

// Better background color - deep space purple with slight warmth
debugObject.clearColor = '#0d0a1a'
gui.addColor(debugObject, 'clearColor').onChange(() => { renderer.setClearColor(debugObject.clearColor) })
renderer.setClearColor(debugObject.clearColor)

/**
 * Particles
 */
let particles = null
let colorAnimationTween = null

// Load models
gltfLoader.load(
    './models.glb',
    (gltf) => {
        particles = {}
        particles.index = 0

        // Positions
        const positions = gltf.scene.children.map(child => child.geometry.attributes.position)

        particles.maxCount = 0
        for(const position of positions)
        {
            if(position.count > particles.maxCount){
                particles.maxCount = position.count
            }
        }

        particles.positions = []
        for(const position of positions)
        {
            const originalArray = position.array
            const newArray = new Float32Array(particles.maxCount * 3)

            for(let i = 0; i < particles.maxCount; i++)
            {
                const i3 = i * 3

                if(i3 < originalArray.length){
                    newArray[i3 + 0] = originalArray[i3 + 0]
                    newArray[i3 + 1] = originalArray[i3 + 1]
                    newArray[i3 + 2] = originalArray[i3 + 2]
                }
                else{
                    const randomIndex = Math.floor(position.count * Math.random()) * 3
                    newArray[i3 + 0] = originalArray[randomIndex + 0]
                    newArray[i3 + 1] = originalArray[randomIndex + 1]
                    newArray[i3 + 2] = originalArray[randomIndex + 2]
                }
            }

            particles.positions.push(new THREE.Float32BufferAttribute(newArray, 3))
        }

        // Geometry
        const sizesArray = new Float32Array(particles.maxCount)

        for(let i = 0; i < particles.maxCount; i++){
            sizesArray[i] = Math.random()
        }

        particles.geometry = new THREE.BufferGeometry()
        particles.geometry.setAttribute('position', particles.positions[particles.index])
        particles.geometry.setAttribute('aPositionTarget', particles.positions[3])
        particles.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizesArray, 1))
        particles.geometry.setIndex(null)

        // Material - warmer orange and brighter blue
        particles.colorA = new THREE.Color('#ff2b00')
        particles.colorB = new THREE.Color('#0048ff')

        particles.material = new THREE.ShaderMaterial({
            vertexShader: particlesVertexShader,
            fragmentShader: particlesFragmentShader,
            uniforms:
            {
                uSize: new THREE.Uniform(0.4),
                uResolution: new THREE.Uniform(new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)),
                uProgress: new THREE.Uniform(0),
                uColorA: new THREE.Uniform(particles.colorA),
                uColorB: new THREE.Uniform(particles.colorB),
                uTime: new THREE.Uniform(0)
            },
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            transparent: true
        })

        // Points
        particles.points = new THREE.Points(particles.geometry, particles.material)
        particles.points.frustumCulled = false
        scene.add(particles.points)

        // Methods
        particles.morph = (index) => {
            // Update attributes
            particles.geometry.attributes.position = particles.positions[particles.index]
            particles.geometry.attributes.aPositionTarget = particles.positions[index]

            // Kill any existing progress animation
            gsap.killTweensOf(particles.material.uniforms.uProgress)

            // Animate uProgress
            gsap.fromTo(
                particles.material.uniforms.uProgress,
                { value: 0 },
                { value: 1, duration: 3, ease: 'linear'}
            )

            // Save index
            particles.index = index
        }
        particles.morph0 = () => { particles.morph(0) }
        particles.morph1 = () => { particles.morph(1) }
        particles.morph2 = () => { particles.morph(2) }
        particles.morph3 = () => { particles.morph(3) }

        // Tweaks
        gui
            .addColor(particles, 'colorA')
            .onChange(() => {
                particles.material.uniforms.uColorA.value.set(particles.colorA)
            })

        gui
            .addColor(particles, 'colorB')
            .onChange(() => {
                particles.material.uniforms.uColorB.value.set(particles.colorB)
            })

        gui
        .add(particles.material.uniforms.uProgress, 'value')
        .min(0)
        .max(1)
        .step(0.001)
        .name('uProgress')
        .listen()

        gui.add(particles, 'morph0')
        gui.add(particles, 'morph1')
        gui.add(particles, 'morph2')

        // Start color animation (optimized)
        animateColors()

        // Complete loader
        completeLoader()
    },
    // Progress callback
    (progress) => {
        if (progress.total > 0) {
            const percent = (progress.loaded / progress.total) * 100
            updateLoader(percent * 0.9) // Reserve last 10% for initialization
        }
    },
    // Error callback
    (error) => {
        console.error('Error loading model:', error)
    }
)

// Optimized color animation - single timeline, longer duration, smoother
function animateColors() {
    if (!particles) return

    // Kill existing animation
    if (colorAnimationTween) {
        colorAnimationTween.kill()
    }

    // Create smooth infinite color animation
    const colorTimeline = gsap.timeline({ repeat: -1 })

    // Define color palette for smooth transitions
    const colorsA = [
        { r: 1.0, g: 0.17, b: 0 },      // #ff2b00 - bright red-orange
        { r: 1.0, g: 0.45, b: 0 },      // orange
        { r: 1.0, g: 0.2, b: 0.4 },     // pink-red
        { r: 0.8, g: 0.1, b: 0.5 },     // magenta
        { r: 1.0, g: 0.17, b: 0 },      // back to start
    ]

    const colorsB = [
        { r: 0, g: 0.28, b: 1.0 },      // #0048ff - bright blue
        { r: 0, g: 0.6, b: 1.0 },       // cyan-blue
        { r: 0.3, g: 0.2, b: 1.0 },     // purple-blue
        { r: 0, g: 0.8, b: 0.8 },       // teal
        { r: 0, g: 0.28, b: 1.0 },      // back to start
    ]

    // Animate through colors smoothly
    for (let i = 0; i < colorsA.length - 1; i++) {
        colorTimeline.to(particles.colorA, {
            duration: 4,
            r: colorsA[i + 1].r,
            g: colorsA[i + 1].g,
            b: colorsA[i + 1].b,
            ease: 'sine.inOut',
            onUpdate: () => {
                particles.material.uniforms.uColorA.value.set(particles.colorA)
            }
        }, i * 4)

        colorTimeline.to(particles.colorB, {
            duration: 4,
            r: colorsB[i + 1].r,
            g: colorsB[i + 1].g,
            b: colorsB[i + 1].b,
            ease: 'sine.inOut',
            onUpdate: () => {
                particles.material.uniforms.uColorB.value.set(particles.colorB)
            }
        }, i * 4)
    }

    colorAnimationTween = colorTimeline
}

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    // Update time uniform
    if (particles) {
        particles.material.uniforms.uTime.value = elapsedTime
    }

    // Update controls
    controls.update()

    // Render normal scene
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()
